"use client";

import * as React from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { useImaAds } from "@/components/ads/use-ima-ads";

/** Demo asset. Swap for the per-video source once the CMS supplies one. */
export const DEMO_VIDEO_SRC =
  "https://static.hkej.com/hkej/images/2026/01/15/4298087_786812f2db76489642f796afa7f32d5f.mp4";

type Props = {
  title: string;
  /** Optional media source; defaults to the demo asset. */
  src?: string;
  poster?: string;
  /** "muted": start the content muted on mount (the legacy player's autoplay); the pre-roll is requested muted too. */
  autoplay?: "muted";
};

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * The /video hero player.
 *
 * Was a static `<Image>` with decorative chrome — the play button only toggled
 * an icon and nudged a fake progress bar, and the 全螢幕 button had no handler
 * at all. QA: "播放器未能播放亦未可以全螢幕播放".
 *
 * The chrome is kept (it is the designed look) but every control now drives a
 * real `<video>`: play/pause, seek by clicking or dragging the bar, mute, and
 * fullscreen on the wrapper so the controls stay visible while full-screen.
 */
export function HeroPlayer({ title, src = DEMO_VIDEO_SRC, poster, autoplay }: Props) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  // GAM in-stream ads (pre-roll on first play, mid-roll at 60 s, post-roll on end —
  // the legacy schedule); inert unless the ad runtime is live for this page.
  const ima = useImaAds({ videoRef, containerRef: wrapRef, autoplayMuted: autoplay === "muted" });
  const imaReset = ima.reset;
  React.useEffect(() => { imaReset(); }, [imaReset, src]);
  const barRef = React.useRef<HTMLDivElement>(null);

  const [playing, setPlaying] = React.useState(false);
  const [muted, setMuted] = React.useState(false);
  const [volume, setVolume] = React.useState(1);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [current, setCurrent] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [pointerInside, setPointerInside] = React.useState(false);

  const progress = duration > 0 ? current / duration : 0;

  /**
   * Chrome visibility.
   *
   * QA: "the controls should disappear when the video is playing and the
   * cursor is not on the video". So: hidden only while playing AND the pointer
   * is elsewhere. Paused always shows them, since a paused player with no
   * visible controls has no affordance to resume.
   *
   * Keyboard users are covered by `focus-within` on the wrapper — see the
   * class list below — so tabbing to a control reveals the bar even though the
   * mouse never entered.
   */
  const chromeHidden = playing && !pointerInside;

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrent(v.currentTime);
    const onMeta = () => setDuration(v.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVolume = () => {
      setMuted(v.muted);
      setVolume(v.volume);
    };

    /**
     * Seed from the element before subscribing.
     *
     * `preload="metadata"` starts fetching as soon as React sets `src` during
     * render, so `loadedmetadata` can fire before this effect runs and attaches
     * the listener. The event is not replayed, so `duration` stayed at its
     * initial 0 and the readout was stuck on "0:00 / 0:00" — QA: "the total
     * time of the video is still 0:00". Reading the live element here closes
     * the race; `readyState >= HAVE_METADATA` means duration is known.
     */
    if (v.readyState >= 1) setDuration(v.duration);
    setCurrent(v.currentTime);
    setMuted(v.muted);
    setVolume(v.volume);
    setPlaying(!v.paused);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("volumechange", onVolume);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("volumechange", onVolume);
    };
  }, []);

  /* Muted autoplay (opt-in): mute, then go through the ad gate so the pre-roll runs first. */
  const onPlayRequested = ima.onPlayRequested;
  React.useEffect(() => {
    if (autoplay !== "muted") return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    setMuted(true);
    void onPlayRequested();
  }, [autoplay, onPlayRequested]);

  React.useEffect(() => {
    const onFs = () => setFullscreen(document.fullscreenElement === wrapRef.current);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void ima.onPlayRequested();
    else v.pause();
  };

  const seekTo = (clientX: number) => {
    const bar = barRef.current;
    const v = videoRef.current;
    if (!bar || !v || !Number.isFinite(v.duration)) return;
    const r = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    v.currentTime = ratio * v.duration;
    setCurrent(v.currentTime);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void wrapRef.current?.requestFullscreen?.();
  };

  return (
    <div
      ref={wrapRef}
      onPointerEnter={() => setPointerInside(true)}
      onPointerMove={() => setPointerInside(true)}
      onPointerLeave={() => setPointerInside(false)}
      className="group/player relative w-full overflow-hidden bg-black"
      style={fullscreen ? undefined : { aspectRatio: "792/445" }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        title={title}
        playsInline
        preload="metadata"
        onClick={togglePlay}
        className="absolute inset-0 size-full object-contain"
      />

      {/* Scrim and controls fade together. `focus-within:` keeps them up for
          keyboard users, whose pointer never enters the player, and
          `pointer-events-none` while hidden stops an invisible bar swallowing
          a click meant for the video. */}
      <div
        className={`absolute inset-x-0 bottom-0 h-[110px] pointer-events-none transition-opacity duration-300 motion-reduce:transition-none group-focus-within/player:opacity-100 ${
          chromeHidden ? "opacity-0" : "opacity-100"
        }`}
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      <div
        className={`absolute left-[24px] right-[24px] bottom-[24px] transition-opacity duration-300 motion-reduce:transition-none group-focus-within/player:pointer-events-auto group-focus-within/player:opacity-100 ${
          chromeHidden ? "opacity-0 pointer-events-none" : "opacity-100 pointer-events-auto"
        }`}
      >
        {/* Seek bar — click or drag anywhere along it. */}
        <div
          ref={barRef}
          role="slider"
          tabIndex={0}
          aria-label="播放進度"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration) || 0}
          aria-valuenow={Math.round(current)}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            seekTo(e.clientX);
          }}
          onPointerMove={(e) => {
            if (e.buttons === 1) seekTo(e.clientX);
          }}
          onKeyDown={(e) => {
            const v = videoRef.current;
            if (!v) return;
            if (e.key === "ArrowRight") v.currentTime = Math.min(v.duration, v.currentTime + 5);
            if (e.key === "ArrowLeft") v.currentTime = Math.max(0, v.currentTime - 5);
          }}
          className="relative h-[4px] w-full cursor-pointer"
        >
          <div
            className="absolute inset-0"
            style={{ backgroundColor: "rgba(255,255,255,0.35)" }}
          />
          <div
            className="absolute left-0 top-0 bottom-0"
            style={{
              width: `${progress * 100}%`,
              backgroundColor: CORE_COLORS.brandGreen,
            }}
          />
          <div
            aria-hidden
            className="absolute size-[12px] rounded-full bg-white top-1/2 -translate-y-1/2 -translate-x-1/2"
            style={{
              left: `${progress * 100}%`,
              boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
            }}
          />
        </div>

        <div className="mt-[14px] flex items-center justify-between">
          <div className="flex items-center gap-[10px]">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? "暫停" : "播放"}
              className="inline-flex size-[40px] items-center justify-center rounded-full transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              {playing ? (
                <Pause className="size-[18px]" fill="white" color="white" strokeWidth={0} />
              ) : (
                <Play className="size-[18px] translate-x-[1px]" fill="white" color="white" strokeWidth={0} />
              )}
            </button>
            <span
              className="tabular-nums text-white"
              style={{
                fontFamily: CORE_FONT.sans,
                fontSize: "13px",
                lineHeight: "18px",
                letterSpacing: "0.2px",
              }}
            >
              {formatTime(current)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center gap-[4px]">
            {/* Mute toggle plus a level slider — QA: "allow adjust volume
                level as well". The slider reveals on hover/focus so the
                resting chrome is unchanged. */}
            <div className="group/vol flex items-center">
              <IconButton
                label={muted ? "取消靜音" : "靜音"}
                onClick={() => {
                  const v = videoRef.current;
                  if (v) v.muted = !v.muted;
                }}
              >
                {muted || volume === 0 ? (
                  <VolumeX className="size-[18px]" color="white" strokeWidth={2} />
                ) : (
                  <Volume2 className="size-[18px]" color="white" strokeWidth={2} />
                )}
              </IconButton>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                aria-label="音量"
                onChange={(e) => {
                  const v = videoRef.current;
                  if (!v) return;
                  const next = Number(e.target.value);
                  v.volume = next;
                  v.muted = next === 0;
                  setVolume(next);
                }}
                className="h-[4px] w-0 cursor-pointer appearance-none rounded-full opacity-0 transition-all duration-200 focus-visible:w-[72px] focus-visible:opacity-100 group-hover/vol:w-[72px] group-hover/vol:opacity-100 group-focus-within/vol:w-[72px] group-focus-within/vol:opacity-100"
                style={{
                  background: `linear-gradient(to right, #ffffff 0%, #ffffff ${
                    (muted ? 0 : volume) * 100
                  }%, rgba(255,255,255,0.35) ${
                    (muted ? 0 : volume) * 100
                  }%, rgba(255,255,255,0.35) 100%)`,
                }}
              />
            </div>
            <IconButton
              label={fullscreen ? "退出全螢幕" : "全螢幕"}
              onClick={toggleFullscreen}
            >
              {fullscreen ? (
                <Minimize2 className="size-[18px]" color="white" strokeWidth={2} />
              ) : (
                <Maximize2 className="size-[18px]" color="white" strokeWidth={2} />
              )}
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-[40px] items-center justify-center rounded-full transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
    >
      {children}
    </button>
  );
}
